import React, { useCallback, useEffect, useRef, useState } from "react";
import { dateTime } from "@/lib/dates";
import { api, API, getToken, TIMEOUTS } from "@/lib/api";
import { Sparkles, X, Send, Brain, Trash2, ChevronDown, History, Loader2, Terminal, Activity, ExternalLink, CalendarDays, Plus, Check, Mic, MicOff, Volume2, VolumeX, Radio } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { ATENA_MODELS, AUTO_MODEL, modelLabel } from "@/lib/atenaModels";
import Markdown from "@/components/Markdown";
import VoicePanel from "@/components/VoicePanel";
import { useListening, useSpeaking } from "@/hooks/useVoice";
import useDictation from "@/hooks/useDictation";
import { toast } from "sonner";

const DOT = { up: "bg-emerald-500", degraded: "bg-amber-500", down: "bg-red-500" };

function agoLabel(iso) {
  if (!iso) return "niciodată";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `acum ${secs}s`;
  if (secs < 3600) return `acum ${Math.round(secs / 60)}m`;
  return `acum ${Math.round(secs / 3600)}h`;
}

// What she is looking at decides what is worth offering. A board open in front
// of the person makes "rezumă conversația din canalul curent" the wrong three
// buttons -- there is no channel, and the board is the thing they are asking about.
function suggestions({ channelName, boardName, canvasName }) {
  if (boardName) {
    return [
      `Cum stă board-ul «${boardName}»?`,
      "Ce e depășit sau fără responsabil aici?",
      `Fă un raport din board-ul «${boardName}»`,
    ];
  }
  if (canvasName) {
    return [
      `Ce scrie pe whiteboard-ul «${canvasName}»?`,
      "Pune un sticky cu decizia de azi",
      "Ce whiteboard-uri mai avem?",
    ];
  }
  const where = channelName ? `#${channelName}` : "canalul curent";
  return [
    `Rezumă conversația din ${where}`,
    `Fă un proces-verbal din ${where}`,
    "Ce tichete sunt deschise?",
  ];
}

function contextLine({ boardName, canvasName }) {
  if (boardName) return `Vede board-ul «${boardName}»`;
  if (canvasName) return `Vede whiteboard-ul «${canvasName}»`;
  return "";
}

export default function AtenaPanel({ workspaceId, channel, board, canvas, onClose, onSwitchToVibe }) {
  const [messages, setMessages] = useState([]);   // {role, content}
  const [text, setText] = useState("");
  const [model, setModel] = useState(() => localStorage.getItem("vs_atena_model") || "");
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [memories, setMemories] = useState([]);
  const [memOpen, setMemOpen] = useState(false);
  const [pending, setPending] = useState(null);   // a tool call awaiting confirmation
  const [lastModel, setLastModel] = useState("");  // what the router actually picked
  const [routeNote, setRouteNote] = useState("");
  const [servers, setServers] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [agenda, setAgenda] = useState(null);       // null = calendar neconectat
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState("");
  const [draft, setDraft] = useState({ title: "", start: "", minutes: 60 });
  const scrollRef = useRef(null);
  const { subscribe } = useWorkspace();

  // Voice. Speaking is remembered because it is a preference; hands-free is not,
  // because a microphone that turns itself on when a panel opens is a surprise
  // nobody asked for twice.
  const [speakReplies, setSpeakReplies] = useState(() => localStorage.getItem("vs_atena_speak") === "1");
  const [handsFree, setHandsFree] = useState(false);
  const [voceDeschisa, setVoceDeschisa] = useState(false);
  // Dictation can be turned off for the whole workspace, so the panel asks the
  // server rather than deciding for itself. Unknown means allowed: a settings
  // call that failed must not silently remove a feature people use.
  const [dictationAllowed, setDictationAllowed] = useState(true);
  const sendRef = useRef(null);
  const voice = useSpeaking();
  const heard = useCallback((said) => {
    // Hands-free heard her name and sends straight through; push-to-talk drops
    // the words in the box, because recognition mishears and the person should
    // see what it thinks they said before it goes anywhere.
    if (handsFree) sendRef.current?.(said);
    else setText((prev) => (prev ? `${prev} ${said}` : said));
  }, [handsFree]);
  const listener = useListening({ onText: heard, handsFree });
  // Dictarea (apăsat, vorbit, dat drumul) se transcrie pe serverul nostru:
  // sunetul nu pleacă nicăieri. Modul „mâini libere" rămâne pe recunoașterea
  // browserului fiindcă are nevoie de ascultare continuă ca să audă cuvântul de
  // trezire — și panoul o spune, în loc s-o ascundă.
  const dictation = useDictation({
    workspaceId,
    onText: (said) => setText((prev) => (prev ? `${prev} ${said}` : said)),
  });
  // One place decides whether there is a microphone at all: the browser has to
  // support it and the workspace has to allow it.
  // Două capacități diferite, cu suport diferit: „mâini libere" are nevoie de
  // ascultare CONTINUĂ, deci de recunoașterea browserului; dictarea apăsată are
  // nevoie doar de un microfon, pe care îl are și Firefox. Confundate sub un
  // singur `supported`, butonul de mâini libere a apărut într-un browser care
  // n-are cu ce asculta.
  const handsFreeSupported = listener.supported && dictationAllowed;
  const mic = handsFree
    ? { ...listener, supported: handsFreeSupported, active: listener.listening }
    : { ...dictation, supported: dictation.supported && dictationAllowed,
        active: dictation.recording };

  // Turned off for the workspace while someone is mid-sentence: hiding the
  // button is not enough, the recogniser has to stop. An owner switching this
  // off means the microphone stops now, not at the next reload.
  const stopListening = listener.stop;
  useEffect(() => {
    if (dictationAllowed) return;
    setHandsFree(false);
    stopListening();
  }, [dictationAllowed, stopListening]);

  const loadSide = useCallback(async () => {
    if (!workspaceId) return;
    try {
      // allSettled, not all: three independent things, and one of them failing
      // must not take the other two with it. Before, two calls shared a fate;
      // adding a third under the same await would have made a settings hiccup
      // empty the conversation list.
      const [c, m, cfg] = await Promise.allSettled([
        api.get("/atena/conversations", { params: { workspace_id: workspaceId } }),
        api.get("/atena/memories", { params: { workspace_id: workspaceId } }),
        api.get("/atena/settings", { params: { workspace_id: workspaceId } }),
      ]);
      if (c.status === "fulfilled") setConversations(c.value.data.slice(0, 5));
      if (m.status === "fulfilled") setMemories(m.value.data);
      // Only an explicit false turns dictation off. A failed call leaves the
      // feature as it was: losing it silently would look like a bug.
      if (cfg.status === "fulfilled") setDictationAllowed(cfg.value.data?.allow_dictation !== false);
    } catch { /* the panel still works without the side lists */ }
  }, [workspaceId]);

  useEffect(() => { loadSide(); }, [loadSide]);

  const loadServers = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await api.get("/status/servers", { params: { workspace_id: workspaceId } });
      setServers(r.data);
    } catch { /* the widget just stays empty */ }
  }, [workspaceId]);

  useEffect(() => { loadServers(); }, [loadServers]);

  const loadAgenda = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const now = new Date();
      const to = new Date(now.getTime() + 48 * 3600 * 1000);
      const r = await api.get("/calendar/events", {
        params: { workspace_id: workspaceId, date_from: now.toISOString(), date_to: to.toISOString() },
      });
      // Serverul spune acum starea, nu o lasă pe seama unui cod de eroare:
      // înainte, „nu ai calendar" venea ca 404, iar clientul îl interpreta.
      setAgenda(r.data?.connected ? (r.data.events || []) : null);
    } catch {
      // Aici ajunge doar un eșec real — rețea, sesiune expirată, server căzut.
      setAgenda(null);
    }
  }, [workspaceId]);

  useEffect(() => { loadAgenda(); }, [loadAgenda]);

  const addEvent = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.start) return;
    setAdding(true);
    try {
      const start = new Date(draft.start);
      const end = new Date(start.getTime() + Number(draft.minutes) * 60000);
      await api.post("/calendar/events",
        { title: draft.title.trim(), start: start.toISOString(), end: end.toISOString() },
        { params: { workspace_id: workspaceId } });
      setDraft({ title: "", start: "", minutes: 60 });
      setAddOpen(false);
      // Confirmation stays in the panel, where the person is looking.
      setLastAdded(`«${draft.title.trim()}» · ${dateTime(start)}`);
      loadAgenda();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu s-a putut adăuga");
    } finally { setAdding(false); }
  };

  // The checker only broadcasts on a transition, so this patches in place rather
  // than refetching the list on every ping.
  useEffect(() => subscribe((ev) => {
    if (ev.type !== "server_status_update") return;
    setServers((prev) => prev.map((s) => s.id === ev.server_id
      ? { ...s, status: ev.status, response_time_ms: ev.response_time_ms, last_checked: new Date().toISOString() }
      : s));
  }), [subscribe]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const pickModel = (value) => {
    setModel(value);
    localStorage.setItem("vs_atena_model", value);
  };

  const send = async (raw) => {
    const message = (raw ?? text).trim();
    if (!message || busy) return;
    setText("");
    setPending(null);
    setRouteNote("");
    setMessages((prev) => [...prev, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      // SSE over fetch: the endpoint streams, and axios would only hand back the
      // whole body at the end.
      const res = await fetch(`${API}/atena/chat?workspace_id=${encodeURIComponent(workspaceId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          message,
          channel_id: channel?.id || null,
          // Context, not a request: the server uses it only when the message
          // does not name a board or whiteboard itself.
          board_id: board?.id || null,
          canvas_id: canvas?.id || null,
          model: model || null,
          conversation_id: conversationId,
        }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let ev;
          try { ev = JSON.parse(payload); } catch { continue; }
          if (ev.type === "meta") {
            setConversationId(ev.conversation_id);
            setLastModel(ev.model_used || "");
            if (ev.note) setRouteNote(ev.note);
          }
          else if (ev.type === "delta") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                ...next[next.length - 1],
                content: next[next.length - 1].content + ev.content,
              };
              return next;
            });
          } else if (ev.type === "replace") {
            // The server rejected what it had already streamed. The deltas are
            // on screen, so it sends the corrected text and the panel adopts it
            // — otherwise a server-side guard is invisible to the reader.
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], content: ev.content };
              return next;
            });
          } else if (ev.type === "done") {
            if (speakReplies && ev.response) voice.speak(ev.response);
          } else if (ev.type === "confirm") setPending(ev.action);
          else if (ev.type === "error") toast.error(ev.content);
        }
      }
      loadSide();
    } catch (e) {
      toast.error(e.message || "Atena nu a răspuns");
    } finally { setBusy(false); }
  };

  // The recogniser is built before send() exists and must not capture a stale
  // copy of it, so it reaches send through a ref that every render refreshes.
  useEffect(() => { sendRef.current = send; });

  // Și „Nu" pleacă la server: un refuz e o decizie, iar decizia intră în jurnal.
  // Panoul nu hotărăște nimic aici — trimite răspunsul omului și arată ce spune
  // serverul.
  const confirmAction = async (yes) => {
    const action = pending;
    setPending(null);
    try {
      const { data } = await api.post("/atena/action",
        { proposal_id: action?.proposal_id, confirmed: yes,
          conversation_id: conversationId },
        { params: { workspace_id: workspaceId }, timeout: TIMEOUTS.atenaAction });
      setMessages((prev) => [...prev, { role: "assistant", content: data.result }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acțiunea a eșuat");
    }
  };

  const resume = (convo) => {
    setConversationId(convo.id);
    setMessages((convo.messages || []).map((m) => ({ role: m.role, content: m.content })));
  };

  const forget = async (id) => {
    try {
      await api.delete(`/atena/memory/${id}`, { params: { workspace_id: workspaceId } });
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut șterge");
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg" data-testid="atena-panel">
      <div className="h-14 px-4 flex items-center gap-2 border-b border-line/10 flex-shrink-0">
        <Sparkles className="w-4 h-4 text-brand" />
        <h2 className="font-bold tracking-tight">ATENA</h2>
        <span className="label-tiny">// ASISTENT AI</span>
        {onSwitchToVibe && (
          <button
            type="button" data-testid="atena-switch-vibe" onClick={onSwitchToVibe} title="Comută pe VIBE.AI"
            className="ml-auto text-fg/40 hover:text-fg p-1"
          ><Terminal className="w-4 h-4" /></button>
        )}
        <button
          type="button" data-testid="atena-voce-comuta" onClick={() => setVoceDeschisa((v) => !v)}
          title="Conversație vorbită" aria-pressed={voceDeschisa}
          className={`p-1 ${voceDeschisa ? "text-brand" : "text-fg/40 hover:text-fg"}`}
        ><Radio className="w-4 h-4" /></button>
        <button type="button" data-testid="atena-close" onClick={onClose} aria-label="Închide panoul" className="text-fg/40 hover:text-fg p-1">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Bucla vorbită, cu starea pe server. Vechiul „hands-free" de mai jos
          folosește recunoașterea browserului — adică trimite sunetul la
          furnizorul lui — și are propriul cuvânt de trezire în JavaScript.
          Sunt două implementări; asta e cea care nu scoate sunetul de pe
          server. Vezi docs/JURNAL_DECIZII_atena.md, D2. */}
      {voceDeschisa && (
        <div className="border-b border-line/10 flex-shrink-0">
          <VoicePanel workspaceId={workspaceId} channelId={channel?.id} />
        </div>
      )}

      <div className="px-4 py-2 border-b border-line/10 flex items-center gap-2 flex-shrink-0">
        <label htmlFor="atena-model" className="label-tiny">MODEL</label>
        <select
          id="atena-model" data-testid="atena-model-select"
          value={model} onChange={(e) => pickModel(e.target.value)}
          className="input-flat text-xs flex-1"
        >
          {[AUTO_MODEL, ...ATENA_MODELS].map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {(lastModel || routeNote) && (
        <div className="px-4 py-1.5 border-b border-line/10 flex-shrink-0" data-testid="atena-route-info">
          {lastModel && (
            <p className="label-tiny text-fg-muted truncate">
              // A RĂSPUNS: {modelLabel(lastModel)}
            </p>
          )}
          {routeNote && <p className="text-[11px] text-amber-500 mt-0.5">{routeNote}</p>}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-xs text-fg-muted leading-relaxed">
              Întreab-o pe Atena despre echipă, canale, tichete, board-uri sau whiteboard-uri.
              Întreab-o „ce poți face?" ca să vezi lista exactă.
            </p>
            {contextLine({ boardName: board?.name, canvasName: canvas?.name }) && (
              <p className="label-tiny text-brand" data-testid="atena-context">
                {contextLine({ boardName: board?.name, canvasName: canvas?.name })}
              </p>
            )}
            <div className="space-y-1.5">
              {suggestions({ channelName: channel?.name, boardName: board?.name, canvasName: canvas?.name }).map((sug) => (
                <button
                  key={sug} type="button"
                  data-testid={`atena-suggestion-${sug.slice(0, 10)}`}
                  onClick={() => send(sug)}
                  className="w-full text-left text-xs px-3 py-2 bg-surface border border-line/10 hover:border-brand/40 transition-colors"
                >{sug}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-full text-sm px-3 py-2 text-left ${
              m.role === "user" ? "bg-surface-2 border border-line/10" : "bg-surface border border-brand/20"}`}>
              {m.role === "assistant" && !m.content && busy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-brand" />
                : <Markdown>{m.content}</Markdown>}
            </div>
          </div>
        ))}
        {pending && (
          <div className="border border-brand/40 bg-brand/10 p-3 space-y-2" data-testid="atena-confirm">
            <p className="text-xs">Confirmi acțiunea?</p>
            <div className="flex gap-2">
              <button type="button" data-testid="atena-confirm-yes" onClick={() => confirmAction(true)} className="btn-primary text-xs px-3 py-1">Da</button>
              <button type="button" data-testid="atena-confirm-no" onClick={() => confirmAction(false)} className="btn-ghost text-xs px-3 py-1">Nu</button>
            </div>
          </div>
        )}
      </div>

      {agenda && (
        <div className="border-t border-line/10 flex-shrink-0">
          <button
            type="button" data-testid="atena-agenda-toggle"
            onClick={() => setAgendaOpen((v) => !v)}
            aria-expanded={agendaOpen}
            className="w-full px-4 py-2 flex items-center gap-2 text-xs text-fg/60 hover:text-fg"
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Agenda ta ({agenda.length})
            <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${agendaOpen ? "rotate-180" : ""}`} />
          </button>
          {agendaOpen && (
            <div className="px-4 pb-3 space-y-2" data-testid="atena-agenda">
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {agenda.length === 0 && <li className="text-xs text-fg-muted">Nimic în următoarele 48 de ore.</li>}
                {agenda.map((ev) => {
                  const s = ev.start ? new Date(ev.start) : null;
                  const e = ev.end ? new Date(ev.end) : null;
                  const mins = s && e ? Math.round((e - s) / 60000) : null;
                  return (
                    <li key={ev.uid} className="flex items-baseline gap-2 text-xs">
                      <span className="mono text-brand flex-shrink-0">
                        {dateTime(s) || "—"}
                      </span>
                      <span className="flex-1 truncate text-fg/80">{ev.title}</span>
                      {mins != null && <span className="text-fg-muted flex-shrink-0">{mins}m</span>}
                    </li>
                  );
                })}
              </ul>
              {lastAdded && (
                <p className="text-[11px] text-brand flex items-center gap-1.5" data-testid="agenda-added">
                  <Check className="w-3 h-3 flex-shrink-0" /> Adăugat: {lastAdded}
                </p>
              )}

              {!addOpen ? (
                <button
                  type="button" data-testid="agenda-open-form"
                  onClick={() => { setAddOpen(true); setLastAdded(""); }}
                  className="w-full text-xs px-3 py-1.5 border border-line/20 text-fg/60 hover:text-fg hover:border-brand/40 transition-colors flex items-center justify-center gap-1.5"
                ><Plus className="w-3.5 h-3.5" /> Eveniment</button>
              ) : (
              <form onSubmit={addEvent} className="space-y-1.5 pt-1 border-t border-line/10">
                <input
                  data-testid="agenda-title" value={draft.title} autoFocus
                  onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
                  placeholder="Titlu eveniment"
                  className="input-flat text-xs w-full"
                />
                <div className="flex gap-1.5">
                  <input
                    data-testid="agenda-start" type="datetime-local" value={draft.start}
                    onChange={(ev) => setDraft({ ...draft, start: ev.target.value })}
                    aria-label="Început" className="input-flat text-xs flex-1"
                  />
                  <select
                    value={draft.minutes} aria-label="Durată"
                    onChange={(ev) => setDraft({ ...draft, minutes: ev.target.value })}
                    className="input-flat text-xs"
                  >{[30, 60, 90, 120].map((m) => <option key={m} value={m}>{m}m</option>)}</select>
                  <button
                    type="submit" data-testid="agenda-add" disabled={adding || !draft.title.trim() || !draft.start}
                    className="btn-primary px-2 disabled:opacity-40"
                    aria-label="Adaugă eveniment"
                  >{adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}</button>
                  <button
                    type="button" onClick={() => setAddOpen(false)}
                    aria-label="Renunţă" className="btn-ghost px-2 text-xs"
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
              </form>
              )}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-line/10 flex-shrink-0">
        <button
          type="button" data-testid="atena-status-toggle"
          onClick={() => setStatusOpen((v) => !v)}
          aria-expanded={statusOpen}
          className="w-full px-4 py-2 flex items-center gap-2 text-xs text-fg/60 hover:text-fg"
        >
          <Activity className="w-3.5 h-3.5" />
          Status servere
          <span className="flex items-center gap-1 ml-1">
            {servers.filter((s) => s.status === "down").length > 0 && (
              <span className={`w-1.5 h-1.5 rounded-full ${DOT.down}`} />
            )}
            {servers.filter((s) => s.status === "degraded").length > 0 && (
              <span className={`w-1.5 h-1.5 rounded-full ${DOT.degraded}`} />
            )}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${statusOpen ? "rotate-180" : ""}`} />
        </button>
        {statusOpen && (
          <ul className="px-4 pb-3 space-y-1.5 max-h-48 overflow-y-auto" data-testid="atena-status-list">
            {servers.length === 0 && <li className="text-xs text-fg-muted">Niciun server configurat.</li>}
            {servers.map((s) => (
              <li key={s.id}>
                <a
                  href={s.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`status-server-${s.id}`}
                  className="flex items-center gap-2 text-xs hover:text-brand group"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT[s.status] || "bg-fg/30"}`} />
                  <span className="flex-1 truncate">{s.name}</span>
                  <span className="text-fg-muted flex-shrink-0">
                    {s.response_time_ms != null ? `${s.response_time_ms}ms` : "—"}
                  </span>
                  <span className="text-fg-muted flex-shrink-0 hidden sm:inline">{agoLabel(s.last_checked)}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-line/10 flex-shrink-0">
        <button
          type="button" data-testid="atena-memory-toggle"
          onClick={() => setMemOpen((v) => !v)}
          aria-expanded={memOpen}
          className="w-full px-4 py-2 flex items-center gap-2 text-xs text-fg/60 hover:text-fg"
        >
          <Brain className="w-3.5 h-3.5" />
          {/* Titlul spune ce e în listă. „Despre tine" era fals când lista
              conținea și memorii de echipă — iar eticheta de pe fiecare rând
              o spunea deja, contrazicând antetul. */}
          Ce știe Atena ({memories.filter((m) => m.scope !== "workspace").length} despre tine,
          {" "}{memories.filter((m) => m.scope === "workspace").length} despre echipă)
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${memOpen ? "rotate-180" : ""}`} />
        </button>
        {memOpen && (
          <ul className="px-4 pb-3 space-y-1.5 max-h-40 overflow-y-auto">
            {memories.length === 0 && <li className="text-xs text-fg-muted">Încă nimic.</li>}
            {memories.map((m) => (
              <li key={m.id} className="flex items-start gap-2 text-xs">
                <span className="label-tiny text-fg-muted uppercase flex-shrink-0 mt-0.5">
                  {m.scope === "workspace" ? "echipă" : "tu"}
                </span>
                <span className="flex-1 text-fg/80">{m.content}</span>
                <button
                  type="button" data-testid={`atena-forget-${m.id}`}
                  onClick={() => forget(m.id)} aria-label="Șterge acest fapt"
                  className="text-fg/30 hover:text-destructive flex-shrink-0"
                ><Trash2 className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {conversations.length > 0 && (
        <div className="border-t border-line/10 px-4 py-2 flex-shrink-0">
          <p className="label-tiny flex items-center gap-1.5 mb-1.5"><History className="w-3 h-3" /> RECENTE</p>
          <ul className="space-y-1">
            {conversations.map((c) => (
              <li key={c.id}>
                <button
                  type="button" data-testid={`atena-resume-${c.id}`} onClick={() => resume(c)}
                  className="w-full text-left text-xs text-fg/60 hover:text-brand truncate"
                >{c.messages?.[0]?.content?.slice(0, 60) || "(conversație goală)"}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(mic.supported || voice.supported) && (
        <div className="border-t border-line/10 px-3 py-1.5 flex items-center gap-3 flex-shrink-0 text-xs">
          {voice.supported && (
            <button
              type="button" data-testid="atena-speak-toggle" aria-pressed={speakReplies}
              onClick={() => {
                const next = !speakReplies;
                setSpeakReplies(next);
                localStorage.setItem("vs_atena_speak", next ? "1" : "0");
                if (!next) voice.cancel();
              }}
              className={`flex items-center gap-1.5 ${speakReplies ? "text-brand" : "text-fg/50 hover:text-fg"}`}
              title="Citește răspunsurile cu voce tare (local, în browser)"
            >
              {speakReplies ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              Citește cu voce
            </button>
          )}
          {voice.speaking && (
            <button type="button" data-testid="atena-stop-speaking" onClick={voice.cancel}
              className="text-fg/50 hover:text-fg">oprește</button>
          )}
          {handsFreeSupported && (
            <button
              type="button" data-testid="atena-handsfree" aria-pressed={handsFree}
              onClick={() => {
                const next = !handsFree;
                setHandsFree(next);
                mic.stop();
                if (!next) return;
                // The recogniser is rebuilt with continuous mode on the next
                // render; starting it here would start the old, single-shot one.
                setTimeout(() => mic.start(), 0);
              }}
              className={`flex items-center gap-1.5 ml-auto ${handsFree ? "text-brand" : "text-fg/50 hover:text-fg"}`}
              title="Ascultă continuu și răspunde când aude „Atena…”"
            ><Radio className="w-3.5 h-3.5" /> Mâini libere</button>
          )}
        </div>
      )}
      {handsFree && (
        <p className="px-3 pb-1 text-[11px] text-fg-muted flex-shrink-0" data-testid="atena-handsfree-note">
          Ascult. Spune „Atena, …" ca să-ți răspund. Dictarea trece prin motorul de
          recunoaștere al browserului — pe Chrome, asta înseamnă serverele Google.
        </p>
      )}
      {dictation.working && !handsFree && (
        <p className="px-3 pb-1 text-[11px] text-fg-muted flex-shrink-0" data-testid="atena-dictation-working">
          Se transcrie pe server…
        </p>
      )}
      {mic.error && (
        <p className="px-3 pb-1 text-[11px] text-red-400 flex-shrink-0" data-testid="atena-mic-error">
          {/* Fără prefix: `useDictation` scrie deja „Microfonul nu a pornit: …",
              iar al doilea îl dubla pe ecran. Mesajul aparține hook-ului, care
              știe ce s-a întâmplat; panoul doar îl arată. */}
          {mic.error}
        </p>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-line/10 p-3 flex items-end gap-2 flex-shrink-0"
      >
        <textarea
          data-testid="atena-input"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2} placeholder="Scrie-i Atenei…"
          className="flex-1 bg-surface-2 border border-line/10 focus:border-line/30 text-sm p-2 outline-none resize-none placeholder-fg/30"
        />
        {mic.supported && (
          <button
            type="button" data-testid="atena-mic" onClick={mic.toggle}
            aria-pressed={mic.active}
            disabled={dictation.working && !handsFree}
            title={mic.active ? "Oprește dictarea"
              : (handsFree ? "Dictează (audio prin motorul browserului)"
                           : "Dictează (se transcrie pe serverul nostru)")}
            className={`p-2 border disabled:opacity-40 ${mic.active ? "border-brand text-brand animate-pulse" : "border-line/20 text-fg/50 hover:text-fg"}`}
          >{mic.active ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}</button>
        )}
        <button
          type="submit" data-testid="atena-send" disabled={busy || !text.trim()}
          className="btn-primary p-2 disabled:opacity-40"
        >{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </form>
    </div>
  );
}
